import { StageId } from './../../entities/project/ProjectStageResponse';
import { R } from '@core/utils/r';
import {
  CompactDealBoardResponse,
  CompactDealResponse,
  CompactDealStageResponse,
  DealBoardId,
  DealBoardResponse,
  DealId,
  DealOrderRequest,
  DealRequest,
  DealResponse,
  DealStageId,
  DealLostReasonId,
  DealLostReasonResponse,
  DealStatus,
  DealStageRequest,
  DealActivityResponse,
  FetchDealBoardRequestExtraParams,
} from '@business/entities/deal';
import { Endpoint } from '@business/api/endpoint';
import { OrgId, CompactOrgResponse } from '@business/entities/organization';
import { TeamId } from '@business/entities/team';
import { CustomFieldService, CommonService } from '@business/services';
import { ProposalTemplateId, ProposalId } from '@business/entities/proposal';

export function fetchDeals(options: { search?: string } = {}) {
  const params = R.objectCompact({ search: options.search });
  return Endpoint.get<CompactDealResponse[]>(`@gc/deals`, { params });
}

export function createDeal(orgId: OrgId, data: DealRequest, proposalId?: ProposalId) {
  if (CustomFieldService.hasFile(data.customFields)) {
    const formData = CommonService.toFormData(data);
    return Endpoint.post<CompactDealResponse>(`@gc/deals/${orgId}`, { formData, hasFiles: true });
  }
  return Endpoint.post<CompactDealResponse>(`@gc/deals/${orgId}`, { data: R.objectCompact({ ...data, proposalId }) });
}

export function updateDeal(dealId: DealId, data: DealRequest) {
  if (CustomFieldService.hasFile(data.customFields)) {
    const formData = CommonService.toFormData(data);
    return Endpoint.post<CompactDealResponse>(`@gc/deals/update-deal/${dealId}`, { formData, hasFiles: true });
  }

  return Endpoint.post<CompactDealResponse>(`@gc/deals/update-deal/${dealId}`, { data });
}

export function deleteDeal(dealId: DealId) {
  return Endpoint.delete<void>(`@gc/deals/${dealId}`);
}

export function fetchDeal(dealId: DealId) {
  return Endpoint.get<DealResponse>(`@gc/deals/deal/${dealId}`);
}

export function updateDealStatus(dealId: DealId, status: DealStatus, lostReason?: string, lostExplain?: string, archiveProposals = false) {
  return Endpoint.post<DealResponse>(`@gc/deals/status/${dealId}`, { data: { status, lostReason, lostExplain, archiveProposals } });
}

export function updateDealStatusMultiple(dealIds: DealId[], status: DealStatus, lostReason?: string, lostExplain?: string) {
  return Endpoint.post<DealResponse>(`@gc/deals/status-multiple`, { data: { status, lostReason, lostExplain, dealIds } });
}

export function updateDealAction(dealId: DealId, actionId: string, actionName: string) {
  return Endpoint.patch<DealResponse>(`@gc/deals/update/${dealId}`, { data: { actionId, actionName } });
}

export function updateClosedDate(id: DealId, dueDate: number) {
  return Endpoint.post<DealResponse>(`@gc/deals/closed-date/${id}`, { data: { dueDate } });
}

export function updateNextContactDate(id: DealId, dueDate: number) {
  return Endpoint.post<DealResponse>(`@gc/deals/next-contact-date/${id}`, { data: { dueDate } });
}

export function updateDealStageMin(dealId: DealId, stageId: string) {
  return Endpoint.patch<CompactDealBoardResponse>(`@gc/deals/deal-stage/${dealId}`, { data: { stageId } });
}
export function updateDealStageFull(dealId: DealId, stageId: string) {
  return Endpoint.patch<DealResponse>(`@gc/deals/deal-stage/${dealId}`, { data: { stageId, full: true } });
}

/** Deal Boards */
export function fetchDealBoards(options: { search?: string } = {}) {
  const params = R.objectCompact({ search: options.search });
  return Endpoint.get<CompactDealBoardResponse[]>(`@gc/deals/boards`, { params });
}

export function createDealBoard(name: string, teams: TeamId[], stages: DealStageRequest[]) {
  return Endpoint.post<CompactDealBoardResponse>(`@gc/deals/board`, { data: { name, teams, stages } });
}

export function updateDealBoard(dealBoardId: DealBoardId, name: string, teams: TeamId[], stages: DealStageRequest[]) {
  return Endpoint.patch<CompactDealBoardResponse>(`@gc/deals/board/${dealBoardId}`, { data: { name, teams, stages } });
}

export function deleteDealBoard(dealBoardId: DealBoardId) {
  return Endpoint.delete<void>(`@gc/deals/board/${dealBoardId}`);
}

export function fetchDealBoard(dealBoardId: DealBoardId, status = DealStatus.inProgress, options?: FetchDealBoardRequestExtraParams) {
  return Endpoint.get<DealBoardResponse>(`@gc/deals/board/${dealBoardId}`, { params: { status, ...options } });
}

export function duplicateDealBoard(dealBoardId: DealBoardId) {
  return Endpoint.post<DealBoardResponse>(`@gc/deals/duplicate-board/${dealBoardId}`);
}

/** Deal Stages */

export function fetchDealStages() {
  return Endpoint.get<CompactDealStageResponse[]>(`@gc/deals/stages`);
}

// export function fetchDealStage(dealStageId: DealStageId) {
//   return Endpoint.get<DealStageResponse>(`@gc/deals/stage/${dealStageId}`);
// }

export function createDealStage(dealBoardId: DealBoardId, title: string, description: string) {
  return Endpoint.post<CompactDealStageResponse>(`@gc/deals/stage/${dealBoardId}`, { data: { title, description } });
}

export function updateDealStage(dealStageId: DealStageId, title: string, boardId: DealBoardId, description: string) {
  return Endpoint.patch<CompactDealStageResponse>(`@gc/deals/stage/${dealStageId}`, { data: { title, boardId, description } });
}

export function deleteDealStage(dealStageId: DealStageId) {
  return Endpoint.delete<void>(`@gc/deals/stage/${dealStageId}`);
}

export function sortDealStages(dealBoardId: DealBoardId, ids: DealStageId[]) {
  return Endpoint.post<void>(`@gc/deals/stage-order/${dealBoardId}`, { data: { ids } });
}

export function sortBoardDeals(dealBoardId: DealBoardId, data: DealOrderRequest) {
  return Endpoint.post<void>(`@gc/deals/order/${dealBoardId}`, { data });
}

/**
 * Deal Reason API
 */

export function createLostReason(name: string) {
  return Endpoint.post<DealLostReasonResponse>(`@gc/deals/lost-reason`, { data: { name } });
}

export function updateLostReason(reasonId: DealLostReasonId, name: string) {
  return Endpoint.patch<DealLostReasonResponse>(`@gc/deals/lost-reason/${reasonId}`, { data: { name } });
}

export function deleteLostReason(reasonId: DealLostReasonId) {
  return Endpoint.delete<void>(`@gc/deals/reason/${reasonId}`);
}

export function fetchLostReasons() {
  return Endpoint.get<DealLostReasonResponse[]>(`@gc/deals/lost-reasons`);
}

export function setVendastaAccounts(orgId: OrgId, id: string, name: string) {
  return Endpoint.post<CompactOrgResponse>(`@gc/organizations/vendasta-account/${orgId}`, { data: { id, name } });
}

export function addProposal(dealId: DealId, ProposalTemplateId: ProposalTemplateId, dealValue = false) {
  return Endpoint.post<DealResponse>(`@gc/deals/add-proposal/${dealId}`, { data: { ProposalTemplateId, dealValue } });
}

export function deleteProposal(dealId: DealId, ProposalTemplateId: ProposalTemplateId) {
  return Endpoint.delete<DealResponse>(`@gc/deals/proposal/${dealId}`, { data: { ProposalTemplateId } });
}

export function getDealTooptip(dealId: DealId) {
  return Endpoint.get<{ str: string }>(`@gc/deals/deal-tooltip/${dealId}`);
}

export function getEventsTooltip(dealId: DealId) {
  return Endpoint.get<{ str: string }>(`@gc/deals/deal-events-tooltip/${dealId}`);
}

export function getActivitiesTooltip(dealId: DealId) {
  return Endpoint.get<DealActivityResponse>(`@gc/deals/deal-activities/${dealId}`);
}
